import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X } from 'lucide-react'
import { search } from '../services/api'
import StaffNotificationCenter from './notifications/StaffNotificationCenter'
import ThemeToggle from './ui/ThemeToggle'
import { useBranding } from '../contexts/BrandingContext'

const Navbar = () => {
  const navigate = useNavigate()
  const { logoUrl } = useBranding()
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState(null)
  const [showResults, setShowResults] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)

  useEffect(() => {
    const searchDebounce = setTimeout(async () => {
      if (searchTerm.length >= 2) {
        setIsSearching(true)
        try {
          const response = await search(searchTerm)
          setSearchResults(response.data)
          setShowResults(true)
        } catch (error) {
          console.error('Search error:', error)
        } finally {
          setIsSearching(false)
        }
      } else {
        setSearchResults(null)
        setShowResults(false)
      }
    }, 300)

    return () => clearTimeout(searchDebounce)
  }, [searchTerm])

  const handleResultClick = (type, id) => {
    setShowResults(false)
    setSearchTerm('')
    if (type === 'villa') {
      navigate(`/villas/${id}`)
    } else if (type === 'owner') {
      navigate(`/owners`)
    } else if (type === 'agent') {
      navigate(`/agents`)
    }
  }

  return (
    <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 lg:px-8 py-3 lg:py-4 relative z-50 overflow-x-hidden max-w-full">
      <div className="flex items-center justify-between gap-2 lg:gap-4">
        {/* Empty spacer on desktop for layout balance */}
        <div className="hidden md:block w-10"></div>

        {/* Centered logo on mobile only */}
        <div className="absolute left-1/2 transform -translate-x-1/2 md:hidden">
          {logoUrl ? (
            <img
              src={logoUrl}
              crossOrigin="anonymous"
              referrerPolicy="no-referrer"
              alt="Villa Experience Logo"
              className="h-9 w-auto object-contain"
            />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-white border border-[#cfa190] flex items-center justify-center text-sm font-semibold text-[#cfa190]">
              VE
            </div>
          )}
        </div>

        {/* Global Search - Desktop */}
        <div className="hidden md:block flex-1 max-w-2xl mx-4 lg:mx-8 relative">
          <div className="relative">
            <input
              type="text"
              placeholder="Search villas, owners, agents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => searchTerm.length >= 2 && setShowResults(true)}
              className="w-full min-h-[44px] px-4 py-2 pl-10 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-[#1e2a2e] dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#cfa190]/50 focus:border-[#cfa190] text-base md:text-sm"
            />
            <Search className="absolute left-3 top-2.5 w-5 h-5 text-slate-400" />
            {isSearching && (
              <div className="absolute right-3 top-2.5">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#cfa190]"></div>
              </div>
            )}
          </div>

          {/* Search Results Dropdown - Desktop */}
          {showResults && searchResults && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-96 overflow-y-auto z-50">
              {/* Villas */}
              {searchResults.villas && searchResults.villas.length > 0 && (
                <div className="p-3 border-b border-slate-200 dark:border-slate-700">
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    Villas ({searchResults.villas.length})
                  </div>
                  {searchResults.villas.map((villa) => (
                    <div
                      key={villa.id}
                      onClick={() => handleResultClick('villa', villa.id)}
                      className="p-2 hover:bg-slate-50 dark:hover:bg-slate-700 rounded cursor-pointer"
                    >
                      <div className="font-medium text-[#1e2a2e] dark:text-white">{villa.name || villa.villaName}</div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">{villa.destination}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Owners */}
              {searchResults.owners && searchResults.owners.length > 0 && (
                <div className="p-3 border-b border-slate-200 dark:border-slate-700">
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    Owners ({searchResults.owners.length})
                  </div>
                  {searchResults.owners.map((owner) => (
                    <div
                      key={owner.id}
                      onClick={() => handleResultClick('owner', owner.id)}
                      className="p-2 hover:bg-slate-50 dark:hover:bg-slate-700 rounded cursor-pointer"
                    >
                      <div className="font-medium text-[#1e2a2e] dark:text-white">{owner.name}</div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">{owner.email}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Agents */}
              {searchResults.agents && searchResults.agents.length > 0 && (
                <div className="p-3">
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    Agents ({searchResults.agents.length})
                  </div>
                  {searchResults.agents.map((agent) => (
                    <div
                      key={agent.id}
                      onClick={() => handleResultClick('agent', agent.id)}
                      className="p-2 hover:bg-slate-50 dark:hover:bg-slate-700 rounded cursor-pointer"
                    >
                      <div className="font-medium text-[#1e2a2e] dark:text-white">{agent.company || agent.admin_name}</div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">{agent.email}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* No Results */}
              {(!searchResults.villas || searchResults.villas.length === 0) &&
               (!searchResults.owners || searchResults.owners.length === 0) &&
               (!searchResults.agents || searchResults.agents.length === 0) && (
                <div className="p-4 text-center text-slate-500 dark:text-slate-400">
                  No results found
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
          {/* Mobile Search Toggle */}
          <button
            onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
            className="md:hidden min-h-[44px] min-w-[44px] p-2 text-slate-500 dark:text-slate-400 hover:text-[#cfa190] hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            aria-label="Toggle search"
          >
            {mobileSearchOpen ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}
          </button>

          <ThemeToggle />
          <StaffNotificationCenter />
        </div>
      </div>

      {/* Mobile Search Expandable */}
      {mobileSearchOpen && (
        <div className="md:hidden mt-3 relative">
          <div className="relative">
            <input
              type="text"
              placeholder="Search villas, owners, agents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => searchTerm.length >= 2 && setShowResults(true)}
              className="w-full px-4 py-2.5 pl-10 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-[#1e2a2e] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#cfa190]/50 focus:border-[#cfa190] text-base"
              autoFocus
            />
            <Search className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
            {isSearching && (
              <div className="absolute right-3 top-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#cfa190]"></div>
              </div>
            )}
          </div>

          {/* Mobile Search Results Dropdown */}
          {showResults && searchResults && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-80 overflow-y-auto z-50">
              {/* Villas */}
              {searchResults.villas && searchResults.villas.length > 0 && (
                <div className="p-3 border-b border-slate-200 dark:border-slate-700">
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    Villas ({searchResults.villas.length})
                  </div>
                  {searchResults.villas.map((villa) => (
                    <div
                      key={villa.id}
                      onClick={() => { handleResultClick('villa', villa.id); setMobileSearchOpen(false); }}
                      className="p-2 hover:bg-slate-50 dark:hover:bg-slate-700 rounded cursor-pointer active:bg-slate-100 dark:active:bg-slate-600"
                    >
                      <div className="font-medium text-[#1e2a2e] dark:text-white">{villa.name || villa.villaName}</div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">{villa.destination}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Owners */}
              {searchResults.owners && searchResults.owners.length > 0 && (
                <div className="p-3 border-b border-slate-200 dark:border-slate-700">
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    Owners ({searchResults.owners.length})
                  </div>
                  {searchResults.owners.map((owner) => (
                    <div
                      key={owner.id}
                      onClick={() => { handleResultClick('owner', owner.id); setMobileSearchOpen(false); }}
                      className="p-2 hover:bg-slate-50 dark:hover:bg-slate-700 rounded cursor-pointer active:bg-slate-100 dark:active:bg-slate-600"
                    >
                      <div className="font-medium text-[#1e2a2e] dark:text-white">{owner.name}</div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">{owner.email}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Agents */}
              {searchResults.agents && searchResults.agents.length > 0 && (
                <div className="p-3">
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    Agents ({searchResults.agents.length})
                  </div>
                  {searchResults.agents.map((agent) => (
                    <div
                      key={agent.id}
                      onClick={() => { handleResultClick('agent', agent.id); setMobileSearchOpen(false); }}
                      className="p-2 hover:bg-slate-50 dark:hover:bg-slate-700 rounded cursor-pointer active:bg-slate-100 dark:active:bg-slate-600"
                    >
                      <div className="font-medium text-[#1e2a2e] dark:text-white">{agent.company || agent.admin_name}</div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">{agent.email}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* No Results */}
              {(!searchResults.villas || searchResults.villas.length === 0) &&
               (!searchResults.owners || searchResults.owners.length === 0) &&
               (!searchResults.agents || searchResults.agents.length === 0) && (
                <div className="p-4 text-center text-slate-500 dark:text-slate-400">
                  No results found
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Navbar
